import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import './SplashScreen.css';

/* ─────────────────────────────────────────────────────────────
   IMAGES — split into two priority buckets:
   • CRITICAL: logo only — loaded eagerly & immediately
   • DEFERRED: product-track images — loaded at idle time
───────────────────────────────────────────────────────────── */
const CRITICAL_IMAGES = ['/images/logo.png', '/gvla-5star/gvla-5star.png'];

const DEFERRED_IMAGES = [
    '/images/tally-cover.png',
    '/images/tss-right.webp',
    '/images/server.webp',
    '/images/ewarehouse.png',
    '/Logistics.png',
    '/images/primeimg/far.png',
    '/UPDATED-IMAGES/pr/pr1.png',
    '/images/web.jpg',
    '/images/service/2.jpg',
    '/images/biometric-time-attendance-500x500.webp',
    '/images/CCTV.png',
];

/* Preload a list of image src strings — returns a Promise */
function preloadImages(srcs, timeoutMs = 1500) {
    return Promise.race([
        Promise.all(srcs.map(src => new Promise(res => {
            const img = new Image();
            img.onload = img.onerror = res;
            img.src = src;
        }))),
        new Promise(res => setTimeout(res, timeoutMs)),
    ]);
}

/* Critical images start immediately at module evaluation (tiny list — logo + badge only) */
const criticalPreloadPromise = preloadImages(CRITICAL_IMAGES, 800);

/* ─────────────────────────────────────────────────────────────
   Framer Motion variants — only used for the EXIT transition.
   Entrance is handled by CSS keyframes to minimise JS overhead
   on the critical render path.
───────────────────────────────────────────────────────────── */
const containerExitVariant = {
    visible: { opacity: 1 },
    exiting: {
        opacity: 0,
        transition: { duration: 0.85, ease: 'easeInOut' },
    },
};

const badgeExit = {
    hidden: { opacity: 0, scale: 0.88, y: 8 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.6, delay: 0.55, ease: [0.22, 1, 0.36, 1] } },
    exiting: { opacity: 0, scale: 0.9, transition: { duration: 0.45, ease: 'easeOut' } },
};

/* ─────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────── */
const SplashScreen = ({ splashState, setSplashState, logoRef, hideLogo }) => {
    const location = useLocation();
    const isDebug = new URLSearchParams(location.search).get('debugSplash') === 'true';

    /* Phase: 'brand' → 'reveal' → (exiting driven by splashState) */
    const [phase, setPhase]           = useState('brand');   // show logo + title immediately
    const [tracksReady, setTracksReady] = useState(false);   // deferred product strip
    const mountedRef                  = useRef(true);

    const isExiting = splashState === 'attaching';

    // ── Easter Egg: 5-click power-user splash skip ──
    const [powerSkipClicks, setPowerSkipClicks] = useState(0);
    const [showPowerToast, setShowPowerToast] = useState(false);
    const powerClickTimer = useRef(null);

    const handlePowerSkip = () => {
        setPowerSkipClicks(prev => {
            const next = prev + 1;
            clearTimeout(powerClickTimer.current);
            powerClickTimer.current = setTimeout(() => setPowerSkipClicks(0), 800);
            if (next >= 5 && splashState === 'loading') {
                setShowPowerToast(true);
                setTimeout(() => {
                    setShowPowerToast(false);
                    setSplashState('attaching');
                }, 900);
                return 0;
            }
            return next;
        });
    };


    /* ── Cleanup ref ── */
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    /* ── Step 1: Wait for critical images (logo + badge), then move to 'reveal' ── */
    useEffect(() => {
        criticalPreloadPromise.then(() => {
            if (!mountedRef.current) return;
            setPhase('reveal');
        });
    }, []);

    /* ── Step 2: Load deferred images at idle time ── */
    useEffect(() => {
        if (phase !== 'reveal') return;
        const load = () => {
            preloadImages(DEFERRED_IMAGES, 2000).then(() => {
                if (!mountedRef.current) return;
                setTracksReady(true);
            });
        };
        // Use idle callback if available, otherwise a small timeout
        if (typeof requestIdleCallback !== 'undefined') {
            const id = requestIdleCallback(load, { timeout: 300 });
            return () => cancelIdleCallback(id);
        } else {
            const t = setTimeout(load, 100);
            return () => clearTimeout(t);
        }
    }, [phase]);

    /* ── Step 3: Trigger exit after min display time (images loaded OR 4.5s hard cap) ── */
    useEffect(() => {
        if (splashState !== 'loading' || isDebug) return;
        // Minimum 2.8s of brand visibility AFTER phase reaches 'reveal'
        if (phase !== 'reveal') return;

        const minTimer = setTimeout(() => {
            if (!mountedRef.current) return;
            setSplashState('attaching');
        }, 4200); // 4.2s total from 'reveal' — gives badge & track time to show

        return () => clearTimeout(minTimer);
    }, [splashState, isDebug, phase, setSplashState]);

    /* ── Step 4: Unmount after exit animation ── */
    useEffect(() => {
        if (splashState !== 'attaching') return;
        const t = setTimeout(() => {
            setSplashState('completed');
            sessionStorage.setItem('splashShown', 'true');
        }, 1800);
        return () => clearTimeout(t);
    }, [splashState, setSplashState]);

    /* ── Compute loader width based on phase ── */
    const loaderDuration = phase === 'brand' ? 0.7 : 4.5;
    const loaderTarget   = isExiting ? '100%' : (phase === 'reveal' ? '92%' : '18%');

    /* ─────────────────────────────────────────────── */
    return (
        <motion.div
            className={`splash-screen${phase === 'reveal' ? ' splash-ready' : ''}`}
            variants={containerExitVariant}
            initial="visible"
            animate={isExiting ? 'exiting' : 'visible'}
            style={{ pointerEvents: isExiting ? 'none' : 'auto' }}
        >
            {/* ── Aurora — CSS animated, delayed 500ms for first-paint perf ── */}
            <div className="splash-aurora" />
            <div className="splash-bg-overlay" />

            <div className="splash-main-composition">

                {/* ── Brand Reveal — pure CSS entrance & exit ── */}
                <div
                    className={`brand-reveal-group${phase === 'reveal' ? ' brand-entered' : ''}${isExiting ? ' brand-exiting' : ''}`}
                >
                    <div className="logo-glow-effect" />
                    <img
                        src="/images/logo.png"
                        alt="iMatrix Logo"
                        className="splash-logo-premium"
                    />

                    <div className="splash-text-stack">
                        <span className={`splash-eyebrow${phase === 'reveal' ? ' text-entered' : ''}`}>
                            Welcome to
                        </span>
                        <h1
                          className="splash-brand-title"
                          onClick={handlePowerSkip}
                          style={{ cursor: 'default', userSelect: 'none' }}
                          title=""
                        >
                            <span className="brand-word brand-word-1">iMatrix</span>
                            <span className="brand-word brand-word-2">Technologies</span>
                            <span className="brand-word brand-word-3">Pvt Ltd</span>
                        </h1>
                    </div>

                    {/* Easter Egg: power-user skip toast */}
                    {showPowerToast && (
                      <div style={{
                        position: 'absolute', bottom: '-48px', left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(0,0,0,0.75)', color: '#00ff46',
                        fontFamily: 'Courier New, monospace', fontSize: '0.78rem',
                        padding: '6px 18px', borderRadius: '999px',
                        border: '1px solid #00ff4640', whiteSpace: 'nowrap',
                        letterSpacing: '0.1em', pointerEvents: 'none'
                      }}>
                        ⚡ Power user detected. Skipping…
                      </div>
                    )}

                </div>

                {/* ── Partner Badge ── */}
                {(splashState === 'loading' || splashState === 'attaching') && (
                    <div className="premium-partner-badge">
                        <motion.div
                            className="badge-glass"
                            variants={badgeExit}
                            initial="hidden"
                            animate={isExiting ? 'exiting' : (phase === 'reveal' ? 'visible' : 'hidden')}
                        >
                            <img
                                ref={logoRef}
                                src="/gvla-5star/gvla-5star.png"
                                alt="Tally 5 Star Partner"
                                className="badge-img"
                                style={{ visibility: hideLogo ? 'hidden' : 'visible' }}
                            />
                            <div className="badge-text-group">
                                <span className="badge-tag">Authorized 5-Star</span>
                                <span className="badge-sub">Tally Partner</span>
                            </div>
                        </motion.div>
                    </div>
                )}

                {/* ── Product Glimpse Tracks — only rendered once deferred images are ready ── */}
                {tracksReady && (
                    <motion.div
                        className="splash-products-glimpse"
                        initial={{ opacity: 0, y: 14 }}
                        animate={isExiting
                            ? { opacity: 0, y: 20, transition: { duration: 0.55, ease: 'easeInOut' } }
                            : { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } }
                        }
                    >
                        {/* Track Left — scrolls left */}
                        <div className="glimpse-track-row track-left">
                            {[...Array(3)].map((_, i) => (
                                <div className="glimpse-content" key={i}>
                                    {[
                                        ['/images/tally-cover.png',  'TallyPrime'],
                                        ['/images/tss-right.webp',   'Tally TSS'],
                                        ['/images/server.webp',      'TallyPrime Server'],
                                        ['/images/ewarehouse.png',   'E-Warehouse'],
                                        ['/Logistics.png',           'Logistics'],
                                        ['/images/primeimg/far.png', 'Fixed Assets'],
                                        ['/UPDATED-IMAGES/pr/pr1.png', 'PR Module'],
                                    ].map(([src, label]) => (
                                        <div className="glimpse-card" key={label}>
                                            <img
                                                src={src}
                                                alt={label}
                                                className="card-bg"
                                                loading="lazy"
                                                onError={e => {
                                                    if (src.includes('far.png')) e.target.src = '/images/far-logo.png';
                                                }}
                                            />
                                            <div className="card-overlay"><span>{label}</span></div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>

                        {/* Track Right — scrolls right */}
                        <div className="glimpse-track-row track-right">
                            {[...Array(3)].map((_, i) => (
                                <div className="glimpse-content" key={i}>
                                    {[
                                        ['/images/web.jpg',          'Web Development'],
                                        ['/images/service/2.jpg',    'IT Services'],
                                        ['/images/biometric-time-attendance-500x500.webp', 'Biometrics'],
                                        ['/images/CCTV.png',         'Security Systems'],
                                    ].map(([src, label]) => (
                                        <div className="glimpse-card" key={label}>
                                            <img src={src} alt={label} className="card-bg" loading="lazy" />
                                            <div className="card-overlay"><span>{label}</span></div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </div>

            {/* ── Loader Bar ── */}
            <motion.div
                className="loader-track"
                animate={{ opacity: isExiting ? 0 : 1 }}
                transition={{ duration: 0.5 }}
            >
                <div className="loader-bar">
                    <motion.div
                        className="loader-fill"
                        initial={{ width: '0%' }}
                        animate={{ width: loaderTarget }}
                        transition={{ duration: loaderDuration, ease: 'easeOut' }}
                    />
                </div>
                <span className="loader-label">
                    {phase === 'brand' ? 'Loading…' : 'Ready'}
                </span>
            </motion.div>
        </motion.div>
    );
};

export default SplashScreen;
